import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { LOW_BALANCE_THRESHOLD_DOLLARS } from '../../constants/billing.constants'
import type {
  BillingState,
  BillingTenant,
  SubscriptionDisplayInfo,
} from '../../interfaces/billing.interface'
import { microsToDollars } from '../../utils/billing.util'

const initialState: BillingState = {
  isLoaded: false,
  tenant: null,
  error: null,
  seatCount: null,
  isAutoTopUpEnabled: false,
}

export const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    setBillingLoading: (state) => {
      state.isLoaded = false
      state.tenant = null
      state.error = null
      state.seatCount = null
      state.isAutoTopUpEnabled = false
    },

    setTenant: (state, action: PayloadAction<BillingTenant>) => {
      state.isLoaded = true
      state.tenant = action.payload
      state.error = null
    },

    setBillingError: (state, action: PayloadAction<string>) => {
      state.isLoaded = true
      state.tenant = null
      state.error = action.payload
    },

    clearBilling: (state) => {
      state.isLoaded = false
      state.tenant = null
      state.error = null
      state.seatCount = null
      state.isAutoTopUpEnabled = false
    },

    updateTenantBalance: (state, action: PayloadAction<string>) => {
      if (state.tenant) {
        state.tenant.balance = action.payload
      }
    },

    setSeatCount: (state, action: PayloadAction<number | null>) => {
      state.seatCount = action.payload
    },

    setAutoTopUpEnabled: (state, action: PayloadAction<boolean>) => {
      state.isAutoTopUpEnabled = action.payload
    },
  },
})

export const {
  setBillingLoading,
  setTenant,
  setBillingError,
  clearBilling,
  updateTenantBalance,
  setSeatCount,
  setAutoTopUpEnabled,
} = billingSlice.actions

export const billingReducer = billingSlice.reducer

export const selectBilling = (state: { billing: BillingState }) => state.billing
export const selectTenant = (state: { billing: BillingState }) => state.billing.tenant
export const selectIsBillingLoaded = (state: { billing: BillingState }) => state.billing.isLoaded
export const selectBillingError = (state: { billing: BillingState }) => state.billing.error
export const selectSeatCount = (state: { billing: BillingState }) => state.billing.seatCount
export const selectIsAutoTopUpEnabled = (state: { billing: BillingState }) =>
  state.billing.isAutoTopUpEnabled

export const selectSubscriptionInfo = createSelector(
  [selectTenant],
  (tenant): SubscriptionDisplayInfo | null => {
    if (!tenant) {
      return null
    }

    return {
      planName: tenant.subscriptionName,
      status: tenant.subscriptionStatus,
      isCancelling: tenant.subscriptionCancelAt !== null,
      subscriptionsEnabled: tenant.subscriptionsEnabled,
    }
  }
)

/**
 * Shortcut for components that only need the feature flag. Defaults to
 * `true` when the tenant hasn't loaded yet — consumers that need to gate
 * subscribe flows should additionally check `isBillingLoaded`.
 */
export const selectSubscriptionsEnabled = createSelector(
  [selectTenant],
  (tenant): boolean => tenant?.subscriptionsEnabled ?? true
)

export const selectIsBalanceDepleted = createSelector([selectTenant], (tenant): boolean => {
  if (!tenant) return false
  return microsToDollars(tenant.balance) <= 0
})

export const selectIsBalanceLow = createSelector([selectTenant], (tenant): boolean => {
  if (!tenant) return false
  return microsToDollars(tenant.balance) <= LOW_BALANCE_THRESHOLD_DOLLARS
})
